import { Types } from 'mongoose';
import { z } from 'zod';
import {
  ArticleArchitectUploadStatus,
  BackfillRunPollStatus,
  ConflictDetectionChannel,
  ConflictResolutionAction,
  ConflictSeverity,
  ConflictType,
  KnowledgeArticleSourceType,
  KnowledgeArticleStatus,
  TicketIngestionStatus,
  TicketingSource,
} from './article-architect.enums';

export const ARTICLE_ARCHITECT_BEDROCK_MAX_FILE_SIZE_BYTES = 4_718_592; // 4.5 MB
// Higher than the platform default (4096) to accommodate multi-article responses from large documents.
export const ARTICLE_ARCHITECT_MAX_OUTPUT_TOKENS = 8192;
export const ARTICLE_ARCHITECT_MAX_FILES = 5;

// Supported document formats: Bedrock DocumentBlock format field values
// ref: https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_DocumentBlock.html
export const ARTICLE_ARCHITECT_ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
] as const;

export const ARTICLE_ARCHITECT_ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
] as const;

// * Optional pagination for list endpoints - omitted params return the full array unchanged.
export const ArticleListPaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).optional(),
});

export const KnowledgeArticleStatsSchema = z.object({
  total: z.number(),
  awaitingReview: z.number(),
});

export const ArticleArchitectBccStatusSchema = z.object({
  bccAddress: z.string(),
  emailCount: z.number(),
  isActive: z.boolean(),
});

export const ArticleArchitectUploadRecordSchema = z.object({
  _id: z.union([z.instanceof(Types.ObjectId), z.string()]),
  companyId: z.string(),
  filename: z.string(),
  fileType: z.string(),
  fileSize: z.number(),
  status: z.enum(ArticleArchitectUploadStatus),
  articlesGenerated: z.number(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const ConflictEvidenceSchema = z.object({
  newArticleText: z.string(),
  existingArticleText: z.string(),
});

export const ArticleConflictSchema = z.object({
  articleId: z.string(),
  articleTitle: z.string(),
  conflictReason: z.string().default(''),
  conflictType: z.enum(ConflictType).optional(),
  severity: z.enum(ConflictSeverity).optional(),
  similarityScore: z.number().optional(),
  detectedBy: z.array(z.enum(ConflictDetectionChannel)).optional(),
  evidence: ConflictEvidenceSchema.optional(),
});

export const ResolveConflictBodySchema = z.object({
  action: z.enum(ConflictResolutionAction),
  existingArticleId: z.string(),
});

export const UpdateKnowledgeArticleBodySchema = z.object({
  status: z.enum(KnowledgeArticleStatus).optional(),
  title: z.string().min(1).optional(),
  subtitle: z.string().optional(),
  summary: z.string().optional(),
  body: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  category: z.string().optional(),
});

export const KnowledgeArticleSchema = z.object({
  _id: z.union([z.instanceof(Types.ObjectId), z.string()]),
  companyId: z.string(),
  sourceType: z.enum(KnowledgeArticleSourceType),
  sourceUploadId: z.string().optional(),
  sourceTicketIds: z.array(z.string()).optional(),
  title: z.string(),
  subtitle: z.string(),
  summary: z.string(),
  body: z.string(),
  keywords: z.array(z.string()),
  language: z.string(),
  category: z.string().optional().default(''),
  status: z.enum(KnowledgeArticleStatus),
  conflicts: z.array(ArticleConflictSchema).optional().default([]),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

// Mirrors ArticleConflictSchema's optional fields (conflictType, severity,
// similarityScore, detectedBy, evidence) instead of only articleId/articleTitle —
// ConflictCard needs the full detection metadata, not just the reason string.
export const ConflictEntrySchema = ArticleConflictSchema.omit({
  articleId: true,
  articleTitle: true,
}).extend({
  existingArticle: KnowledgeArticleSchema,
});

export const ConflictGroupSchema = z.object({
  newArticle: KnowledgeArticleSchema,
  conflictingArticles: z.array(ConflictEntrySchema),
});

// * Query for the ticket-ingestion status poll. Source defaults to TopDesk, the
// * only source live in Phase 1.
export const TicketIngestionStatusQuerySchema = z.object({
  source: z.enum(TicketingSource).optional().default(TicketingSource.Topdesk),
});

// * Current/last incident-pull run for a (companyId, source). Optional fields are
// * absent on an idle run and filled in as the run progresses and settles.
export const TicketIngestionStatusResponseSchema = z.object({
  status: z.enum(TicketIngestionStatus),
  startedAt: z.date().optional(),
  lastHeartbeatAt: z.date().optional(),
  finishedAt: z.date().optional(),
  incidentCount: z.number().optional(),
  processed: z.number(),
  total: z.number(),
  error: z.string().optional(),
  lastCouldNotProcessCount: z.number().optional(),
});

// * Latest backfill run for a (companyId, source), polled by the generation
// * overlay so a dropped socket still surfaces a terminal outcome. Soft-null: no
// * run reports `idle` with a zero word total, never a throw. `clusterCount` and
// * `error` are absent until the run records them.
export const BackfillRunStatusResponseSchema = z.object({
  status: z.enum(BackfillRunPollStatus),
  wordTotal: z.number(),
  clusterCount: z.number().optional(),
  error: z.string().optional(),
});

// * Count of incidents completed since the last sync cursor, for the weekly-sync
// * card. `hasCursor` false marks a first run with no prior sync, where the count is
// * always 0. A null `newIncidentsSinceCursor` is a soft failure - the cursor exists
// * but the count could not be read (a source outage) - so the card degrades instead
// * of breaking.
export const TicketIncrementalCountResponseSchema = z.object({
  hasCursor: z.boolean(),
  newIncidentsSinceCursor: z.number().nullable(),
});
